import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TablePaginationConfig, TableProps } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';

import { useConditionalTableScroll } from '@/shared/hooks/useConditionalTableScroll';
import { DEFAULT_PAGE_SIZE, PAGINATION_OPTIONS } from '@/shared/lib/constants';

import { type ConnectorListParams, type ConnectorSortField, type SortOrder, useConnectors } from '@/entities/connector';

import type { ConnectorTableRow } from './types';

const TABLE_VIEWPORT_OFFSET = 420;
const TABLE_ROW_HEIGHT = 40;
const TABLE_HEADER_HEIGHT = 40;
const TABLE_PAGINATION_HEIGHT = 64;

type UseConnectorTableParams = {
  isActive?: boolean;
  status?: ConnectorListParams['status'];
};

export const useConnectorTable = ({ isActive, status }: UseConnectorTableParams) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<ConnectorSortField | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<SortOrder | undefined>(undefined);

  const listParams = useMemo<ConnectorListParams>(
    () => ({
      page: currentPage,
      pageSize: currentPageSize,
      isActive,
      status,
      sortBy,
      sortOrder,
    }),
    [currentPage, currentPageSize, isActive, status, sortBy, sortOrder]
  );

  const { data: connectorListResponse, isLoading } = useConnectors(listParams);
  const connectors = useMemo(() => connectorListResponse?.items ?? [], [connectorListResponse]);
  const connectorMap = useMemo(() => new Map(connectors.map((connector) => [connector.id, connector])), [connectors]);

  const total = connectorListResponse?.page.total ?? 0;

  const dataSource = useMemo<ConnectorTableRow[]>(
    () =>
      connectors.map((connector) => ({
        key: connector.id,
        id: connector.id,
        name: connector.name,
        workerName: connector.worker?.name,
        status: connector.status,
        isActive: connector.isActive,
      })),
    [connectors]
  );

  const handlePaginationChange = useCallback((page: number, pageSize: number) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  }, []);

  const handleTableChange: TableProps<ConnectorTableRow>['onChange'] = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, unknown>,
      sorter: SorterResult<ConnectorTableRow> | SorterResult<ConnectorTableRow>[]
    ) => {
      const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const newSortBy = singleSorter.order ? (singleSorter.columnKey as ConnectorSortField) : undefined;
      const newSortOrder = singleSorter.order ? (singleSorter.order === 'ascend' ? 'asc' : 'desc') : undefined;

      const isSortChanged = newSortBy !== sortBy || newSortOrder !== sortOrder;

      setSortBy(newSortBy);
      setSortOrder(newSortOrder);

      if (isSortChanged) {
        setCurrentPage(1);
      }
    },
    [sortBy, sortOrder]
  );

  const paginationConfig = useMemo<TablePaginationConfig>(
    () => ({
      current: currentPage,
      pageSize: currentPageSize,
      total,
      pageSizeOptions: [...PAGINATION_OPTIONS],
      showSizeChanger: true,
      locale: { items_per_page: '' },
      showTotal: (total, range) => `(${total}개 중 ${range[0]}-${range[1]})`,
      onChange: handlePaginationChange,
    }),
    [total, currentPage, currentPageSize, handlePaginationChange]
  );

  const scrollConfig = useConditionalTableScroll({
    dataLength: dataSource.length,
    pageSize: currentPageSize,
    viewportOffset: TABLE_VIEWPORT_OFFSET,
    rowHeight: TABLE_ROW_HEIGHT,
    headerHeight: TABLE_HEADER_HEIGHT,
    paginationHeight: TABLE_PAGINATION_HEIGHT,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [isActive, status]);

  return {
    connectors,
    connectorMap,
    dataSource,
    paginationConfig,
    scrollConfig,
    isLoading,
    sortBy,
    sortOrder,
    handleTableChange,
  } as const;
};
